
import React, { useEffect } from 'react';
import { useCountryRedirect } from '@/hooks/useCountryRedirect';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useQueryParam } from '@/hooks/use-query-param';
import { CountrySelectionDialog } from '@/components/country/CountrySelectionDialog';
import { Button } from '@/components/ui/button';
import { Home } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { hasLaunchDatePassed } from '@/utils/date-constants';

interface CountryRedirectWrapperProps {
  children: React.ReactNode;
  shouldRedirect?: boolean;
}

const CountryRedirectWrapper: React.FC<CountryRedirectWrapperProps> = ({ 
  children, 
  shouldRedirect = false // Changed default to false
}) => {
  const [debug] = useQueryParam('debug-geo');
  const showDebug = debug === 'true';
  const navigate = useNavigate();
  const location = useLocation();
  const bypassWaitlist = location.search.includes('bypass-waitlist');
  const previewWaitlist = location.search.includes('preview-waitlist');
  const skipRedirect = location.search.includes('skip-redirect');
  
  // Modified to use country redirect but not force users to the waitlist
  const { geoLocation, isLoading, error } = useCountryRedirect(shouldRedirect);
  
  const detectedCountry = geoLocation?.countryCode ? {
    code: geoLocation.countryCode,
    name: geoLocation.countryName || geoLocation.countryCode
  } : undefined;

  // Function to clear the redirect flag - useful for manual navigation
  const clearRedirectFlag = () => {
    sessionStorage.removeItem('initial-redirect-occurred');
    sessionStorage.removeItem('redirect-timestamp');
    localStorage.removeItem('cayman-redirect-attempts');
    localStorage.removeItem('cayman-redirected');
  };

  return (
    <>
      {showDebug && (
        <Alert className="max-w-md mx-auto mt-4 mb-2">
          <AlertDescription>
            <strong>Geolocation Debug:</strong><br />
            {isLoading ? 'Loading...' : 
              error ? `Error: ${error}` : 
              geoLocation ? 
                `Detected: ${geoLocation.countryCode} (${geoLocation.city}, ${geoLocation.regionCode})` : 
                'No location data'
            }
            <div className="mt-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearRedirectFlag}
                className="text-xs"
              >
                Reset Redirect
              </Button>
              <Link to="/?skip-redirect=true">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="ml-2 text-xs"
                >
                  <Home className="h-3 w-3 mr-1" />
                  Home (No Redirect)
                </Button>
              </Link>
              {bypassWaitlist && (
                <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                  Bypass Active
                </span>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}
      {/* Pass detected country but don't auto-open dialog */}
      <CountrySelectionDialog detectedCountry={detectedCountry} forceOpen={false} />
      {children}
    </>
  );
};

export default CountryRedirectWrapper;
